import { useCallback, useEffect, useRef, useState } from 'react';

import type { SheetAppointment, SheetReport } from '../lib/crm/accountSheet';
import { opportunityFromRow } from '../lib/crm/opportunityRow';
import { dealContacts, type DealContact } from '../lib/crm/opportunitySheet';
import { logWarn } from '../lib/logger';
import { supabase } from '../lib/supabase';
import type { CRMOpportunity, CRMPipelineStage } from '../types/crm';
import type { CRMNote } from '../types/notes';

/** The deal as its sheet reads it: the row, its stage, its account's name, its product, its lead. */
export type SheetOpportunity = Omit<CRMOpportunity, 'stage' | 'account' | 'contacts' | 'stripe_product'> & {
  stage: CRMPipelineStage | null;
  accountRef: { id: string; name: string } | null;
  /** The catalogue's product (`stripe_product_id`), by name. */
  product: { id: string; name: string } | null;
  lead: { id: string; company_name: string | null } | null;
};

export interface OpportunityRows {
  contacts: DealContact[];
  notes: CRMNote[];
  appointments: SheetAppointment[];
  reports: SheetReport[];
}

export interface OpportunitySheetData {
  opportunity: SheetOpportunity | null;
  /** The pipeline's stages (to move the deal, to close it). */
  stages: CRMPipelineStage[];
  /** No such deal: the sheet says so. */
  missing: boolean;
  /** The deal could not be read: the sheet says that, not « introuvable » (beside it on a re-read). */
  error: string | null;
  rows: OpportunityRows;
  /** The rows are this deal's (never « Aucun… » before they are). */
  rowsReady: boolean;
  rowsError: string | null;
  /** Read the deal again after a change (its stage, status, figures). */
  reload: () => Promise<void>;
}

const NO_ROWS: OpportunityRows = { contacts: [], notes: [], appointments: [], reports: [] };
const CONTACT_FIELDS = 'id, first_name, last_name, title, email, phone';

/**
 * The deal and what it is joined to — each by its only key. Its lead is read on its own
 * (`readLead`): two keys tie a deal to a lead (the deal's `onboarding_lead_id`, the lead's
 * `crm_opportunity_id`) and PostgREST refuses an embed that does not name one (PGRST201) — the
 * sheet could never have been read (relecture du 13/10/2026).
 */
export const OPPORTUNITY_SELECT = '*, stage:crm_pipeline_stages(*), account:crm_accounts(id, name), stripe_product:stripe_products(id, name)';

/** The deal's onboarding lead by its key; unreadable, still there by its key (its sheet opens). */
async function readLead(dealId: string, leadId: string | null): Promise<SheetOpportunity['lead']> {
  if (!leadId) return null;
  const { data, error } = await supabase.from('onboarding_leads').select('id, company_name').eq('id', leadId).maybeSingle();
  if (error) logWarn('crm', 'opportunity sheet: lead unreadable', { opportunityId: dealId, error: error.message });
  return data ?? { id: leadId, company_name: null };
}

async function readOpportunity(id: string): Promise<{ opportunity: SheetOpportunity | null; error: string | null }> {
  const { data, error } = await supabase
    .from('crm_opportunities')
    .select(OPPORTUNITY_SELECT)
    .eq('id', id)
    .maybeSingle();
  if (error) return { opportunity: null, error: error.message };
  if (!data) return { opportunity: null, error: null };
  const { stage, account, stripe_product, ...row } = data;
  const lead = await readLead(id, row.onboarding_lead_id);
  return {
    opportunity: {
      ...opportunityFromRow(row),
      stage: stage ? { ...stage, created_at: stage.created_at ?? '', updated_at: stage.updated_at ?? '' } : null,
      accountRef: account ? { id: account.id, name: account.name } : null,
      product: stripe_product ?? null,
      lead,
    },
    error: null,
  };
}

async function readStages(): Promise<CRMPipelineStage[]> {
  const { data, error } = await supabase.from('crm_pipeline_stages').select('*').order('order_index');
  if (error) throw new Error(error.message);
  return (data ?? []).map((s) => ({ ...s, created_at: s.created_at ?? '', updated_at: s.updated_at ?? '' }));
}

async function readRows(id: string, accountId: string): Promise<OpportunityRows> {
  const [named, ofAccount, notes, appointments] = await Promise.all([
    supabase.from('crm_opportunity_contacts').select(`role, contact:crm_contacts(${CONTACT_FIELDS})`).eq('opportunity_id', id),
    supabase.from('crm_contacts').select(CONTACT_FIELDS).eq('account_id', accountId).eq('status', 'active').order('last_name'),
    supabase.from('crm_notes').select('*').eq('opportunity_id', id).order('created_at', { ascending: false }).limit(10),
    supabase
      .from('appointments')
      .select('id, start_time, title, status, appointment_type:appointment_types(name), contact:crm_contacts(first_name, last_name)')
      .eq('opportunity_id', id)
      .order('start_time', { ascending: false })
      .limit(10),
  ]);
  const failed = named.error ?? ofAccount.error ?? notes.error ?? appointments.error;
  if (failed) throw new Error(failed.message);
  // The meeting reports of its appointments and of its notes. Optional: unreadable, the rest shows.
  const filters = [
    (appointments.data ?? []).length > 0 ? `appointment_id.in.(${(appointments.data ?? []).map((a) => a.id).join(',')})` : null,
    (notes.data ?? []).length > 0 ? `note_id.in.(${(notes.data ?? []).map((n) => n.id).join(',')})` : null,
  ].filter((f): f is string => f !== null);
  let reports: SheetReport[] = [];
  if (filters.length > 0) {
    const res = await supabase.from('crm_meeting_reports').select('id, cr_title, created_at').or(filters.join(',')).order('created_at', { ascending: false }).limit(5);
    if (res.error) logWarn('crm', 'opportunity sheet: meeting reports unreadable', { opportunityId: id, error: res.error.message });
    else reports = (res.data ?? []).map((r) => ({ id: r.id, cr_title: r.cr_title, created_at: r.created_at ?? '' }));
  }
  return {
    contacts: dealContacts(
      (named.data ?? []).map((r) => ({ role: r.role, contact: r.contact })),
      ofAccount.data ?? [],
    ),
    notes: (notes.data ?? []).map((n) => ({ ...n, created_at: n.created_at ?? '' })) as unknown as CRMNote[],
    appointments: (appointments.data ?? []).map((a) => ({
      id: a.id,
      start_time: a.start_time,
      status: a.status,
      contact: a.contact,
      // An appointment without a title says its type (« Démo »).
      title: a.title?.trim() || (a.appointment_type as { name?: string | null } | null)?.name || null,
    })),
    reports,
  };
}

/**
 * A deal's working sheet (PLAN-ENCRE-OUTILS O3): the deal (stage, account, product, onboarding lead),
 * the pipeline's stages, then its contacts — named on it first, then its account's — its notes,
 * appointments and meeting reports. Every answer belongs to ONE deal: a late one for a deal left
 * since is dropped, and of two reads of the same deal the older one arriving last too.
 */
export function useOpportunitySheet(opportunityId: string | null): OpportunitySheetData {
  const [opportunity, setOpportunity] = useState<SheetOpportunity | null>(null);
  const [stages, setStages] = useState<CRMPipelineStage[]>([]);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<OpportunityRows>(NO_ROWS);
  const [rowsFor, setRowsFor] = useState<string | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const latest = useRef<string | null>(null);
  const readCount = useRef(0);

  const loadOpportunity = useCallback(async (id: string) => {
    const ticket = ++readCount.current;
    const read = await readOpportunity(id);
    if (latest.current !== id || ticket !== readCount.current) return;
    if (read.error) logWarn('crm', 'opportunity sheet: deal unreadable', { opportunityId: id, error: read.error });
    // A failed RE-read keeps the deal shown (and the form open on it): the error is said beside it.
    setOpportunity((shown) => (read.error && shown?.id === id ? shown : read.opportunity));
    setError(read.error);
    setMissing(!read.error && !read.opportunity);
  }, []);

  useEffect(() => {
    latest.current = opportunityId;
    setOpportunity(null);
    setMissing(false);
    setError(null);
    setRows(NO_ROWS);
    setRowsFor(null);
    setRowsError(null);
    if (!opportunityId) return;
    readStages()
      .then(setStages)
      .catch((err: unknown) => logWarn('crm', 'opportunity sheet: stages unreadable', { error: err instanceof Error ? err.message : String(err) }));
    void loadOpportunity(opportunityId);
  }, [opportunityId, loadOpportunity]);

  // The rows once the deal is read (its account's contacts need its account); not again on a reload.
  const accountId = opportunity && opportunity.id === opportunityId ? opportunity.account_id : null;
  useEffect(() => {
    if (!opportunityId || !accountId) return;
    const id = opportunityId;
    readRows(id, accountId)
      .then((read) => {
        if (latest.current !== id) return;
        setRows(read);
        setRowsFor(id);
      })
      .catch((err: unknown) => {
        if (latest.current !== id) return;
        const message = err instanceof Error ? err.message : String(err);
        logWarn('crm', 'opportunity sheet: rows unreadable', { opportunityId: id, error: message });
        setRowsError(message);
      });
  }, [opportunityId, accountId]);

  const reload = useCallback(async () => {
    if (opportunityId) await loadOpportunity(opportunityId);
  }, [opportunityId, loadOpportunity]);

  return { opportunity, stages, missing, error, rows, rowsReady: opportunityId !== null && rowsFor === opportunityId, rowsError, reload };
}
